"use client";

import { Suspense, useEffect, useState, useCallback } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { useSession } from "next-auth/react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const SUBSPECIALTY_LABELS: Record<string, string> = {
  oculoplastics: "성형안과",
  glaucoma: "녹내장",
  cornea: "각막/외안부",
  strabismus: "사시",
  "neuro-ophthalmology": "신경안과",
  lens: "백내장/수정체",
  retina: "망막",
  uveitis: "포도막",
  refraction: "굴절",
  pharmacology: "안약리",
  pathology: "안병리",
  tumor: "종양",
  pediatric: "소아안과",
  general: "기타",
};

interface YearMeta {
  year: number;
  count: number;
}
interface SubspecMeta {
  subspecialty: string;
  count: number;
}

interface SavedProgress {
  url: string;
  questionIds: string[];
  answers: [string, number][];
  flagged: string[];
  currentIdx: number;
  elapsedSeconds: number;
  savedAt: number;
  label: string;
}

export default function ExamsPageWrapper() {
  return (
    <Suspense fallback={<div className="flex items-center justify-center min-h-[60vh]"><div className="text-muted-foreground">불러오는 중...</div></div>}>
      <ExamsPage />
    </Suspense>
  );
}

function ExamsPage() {
  const { data: session } = useSession();
  const router = useRouter();
  const searchParams = useSearchParams();

  const initMode = (searchParams.get("mode") as "year" | "subspecialty" | "random") || "year";
  const initSubspec = searchParams.get("sub") || null;
  const initTopics = searchParams.get("topics") ? new Set(searchParams.get("topics")!.split(",")) : new Set<string>();

  const [years, setYears] = useState<YearMeta[]>([]);
  const [subspecialties, setSubspecialties] = useState<SubspecMeta[]>([]);
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [selectedSubspec, setSelectedSubspec] = useState<string | null>(initSubspec);
  const [questionCount, setQuestionCount] = useState(0);
  const [mode, setMode] = useState<"year" | "subspecialty" | "random">(initMode);
  const [randomLimit, setRandomLimit] = useState(30);
  const [randomSubspec, setRandomSubspec] = useState<string | null>(null);
  const [topicsBySubspec, setTopicsBySubspec] = useState<Record<string, { topic: string; count: number }[]>>({});
  const [selectedTopics, setSelectedTopics] = useState<Set<string>>(initTopics);
  const [loading, setLoading] = useState(true);
  const [savedProgress, setSavedProgress] = useState<SavedProgress | null>(null);

  const updateURL = useCallback((m: string, sub: string | null, topics: Set<string>) => {
    const params = new URLSearchParams();
    if (m !== "year") params.set("mode", m);
    if (sub) params.set("sub", sub);
    if (topics.size > 0) params.set("topics", Array.from(topics).join(","));
    const qs = params.toString();
    router.replace(`/exams${qs ? "?" + qs : ""}`, { scroll: false });
  }, [router]);

  useEffect(() => {
    fetch("/api/exams/meta")
      .then((r) => r.json())
      .then((data) => {
        setYears(data.years);
        setSubspecialties(data.subspecialties);
        setTopicsBySubspec(data.topicsBySubspec || {});
        setLoading(false);
      });
    // Check for saved quiz progress
    try {
      const raw = localStorage.getItem("exam-quiz-progress");
      if (raw) {
        const saved: SavedProgress = JSON.parse(raw);
        // Only show if less than 24 hours old
        if (Date.now() - saved.savedAt < 24 * 60 * 60 * 1000) {
          setSavedProgress(saved);
        } else {
          localStorage.removeItem("exam-quiz-progress");
        }
      }
    } catch {}
  }, []);

  useEffect(() => {
    if (mode === "year" && selectedYear) {
      const y = years.find((y) => y.year === selectedYear);
      setQuestionCount(y?.count || 0);
    } else if (mode === "subspecialty" && selectedSubspec) {
      if (selectedTopics.size > 0) {
        const topics = topicsBySubspec[selectedSubspec] || [];
        const count = topics
          .filter((t) => selectedTopics.has(t.topic))
          .reduce((sum, t) => sum + t.count, 0);
        setQuestionCount(count);
      } else {
        const s = subspecialties.find((s) => s.subspecialty === selectedSubspec);
        setQuestionCount(s?.count || 0);
      }
    } else if (mode === "random") {
      setQuestionCount(randomLimit);
    }
  }, [mode, selectedYear, selectedSubspec, selectedTopics, years, subspecialties, randomLimit, topicsBySubspec]);

  const startExam = () => {
    const params = new URLSearchParams();
    if (mode === "year" && selectedYear) {
      params.set("year", String(selectedYear));
    } else if (mode === "subspecialty" && selectedSubspec) {
      params.set("subspecialty", selectedSubspec);
      if (selectedTopics.size > 0) {
        params.set("topics", Array.from(selectedTopics).join(","));
      }
    } else if (mode === "random") {
      params.set("random", "true");
      params.set("limit", String(randomLimit));
      if (randomSubspec) params.set("subspecialty", randomSubspec);
    } else {
      return;
    }
    // Clear saved progress when starting a new exam
    localStorage.removeItem("exam-quiz-progress");
    setSavedProgress(null);
    router.push(`/exams/quiz?${params.toString()}`);
  };

  const resumeExam = () => {
    if (!savedProgress) return;
    const params = new URLSearchParams(savedProgress.url);
    params.set("resume", "true");
    router.push(`/exams/quiz?${params.toString()}`);
  };

  const discardProgress = () => {
    localStorage.removeItem("exam-quiz-progress");
    setSavedProgress(null);
  };

  const formatElapsed = (secs: number) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const totalQuestions = years.reduce((sum, y) => sum + y.count, 0);

  return (
    <div className="max-w-4xl mx-auto py-8 px-4">
      <div className="mb-8">
        <h1 className="text-2xl font-bold mb-2">기출문제 풀기</h1>
        <p className="text-muted-foreground">
          안과 전문의 1차 시험 기출문제 {totalQuestions}문항
        </p>
      </div>

      {/* 이어풀기 배너 */}
      {savedProgress && session?.user && (
        <div className="mb-6 p-4 rounded-lg border border-primary/30 bg-primary/5">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-medium mb-1">
                풀던 시험이 있습니다
              </div>
              <div className="text-xs text-muted-foreground">
                {savedProgress.label} &middot;{" "}
                {savedProgress.answers.length}/{savedProgress.questionIds.length}문항 답변 &middot;{" "}
                경과시간 {formatElapsed(savedProgress.elapsedSeconds)}
              </div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={resumeExam}>
                이어풀기
              </Button>
              <Button size="sm" variant="ghost" onClick={discardProgress}>
                삭제
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* 시험 모드 선택 (로그인 필수) */}
      {!session?.user ? null : <>
      <div className="flex gap-2 mb-6">
        {(["year", "subspecialty", "random"] as const).map((m) => (
          <Button
            key={m}
            variant={mode === m ? "default" : "outline"}
            onClick={() => {
              setMode(m);
              setSelectedYear(null);
              setSelectedSubspec(null);
              setSelectedTopics(new Set());
              updateURL(m, null, new Set());
            }}
          >
            {m === "random" ? "랜덤 모의고사" : m === "year" ? "연도별" : "분과별"}
          </Button>
        ))}
      </div>

      {/* 랜덤 모의고사 */}
      {mode === "random" && (
        <div className="mb-6 space-y-4">
          <div className="grid grid-cols-4 gap-3">
            {[
              { limit: 30, time: "~25분" },
              { limit: 60, time: "~50분" },
              { limit: 90, time: "~75분" },
              { limit: 120, time: "~100분" },
            ].map(({ limit, time }) => (
              <button
                key={limit}
                onClick={() => setRandomLimit(limit)}
                className={`p-4 rounded-lg border text-center transition-all hover:shadow-md hover:-translate-y-0.5 ${
                  randomLimit === limit
                    ? "border-primary bg-primary/10 ring-2 ring-primary/30"
                    : "border-border hover:border-primary/50"
                }`}
              >
                <div className="text-2xl font-bold">{limit}</div>
                <div className="text-xs text-muted-foreground">문제</div>
                <div className="text-[10px] text-muted-foreground/60 mt-1">{time}</div>
              </button>
            ))}
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-2">분과 선택 (선택사항)</p>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setRandomSubspec(null)}
                className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                  !randomSubspec ? "bg-primary text-primary-foreground" : "hover:bg-accent"
                }`}
              >
                전체
              </button>
              {subspecialties.map((s) => (
                <button
                  key={s.subspecialty}
                  onClick={() => setRandomSubspec(s.subspecialty)}
                  className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                    randomSubspec === s.subspecialty
                      ? "bg-primary text-primary-foreground"
                      : "hover:bg-accent"
                  }`}
                >
                  {SUBSPECIALTY_LABELS[s.subspecialty] || s.subspecialty}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* 연도 선택 */}
      {mode === "year" && (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3 mb-6">
          {years.map((y) => (
            <button
              key={y.year}
              onClick={() => setSelectedYear(y.year)}
              className={`p-4 rounded-lg border text-center transition-colors ${
                selectedYear === y.year
                  ? "border-primary bg-primary/10 text-primary font-semibold"
                  : "border-border hover:border-primary/50"
              }`}
            >
              <div className="text-lg font-medium">{y.year}</div>
              <div className="text-sm text-muted-foreground">{y.count}문항</div>
            </button>
          ))}
        </div>
      )}

      {/* 분과 선택 */}
      {mode === "subspecialty" && (
        <div className="mb-6 space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {[...subspecialties].sort((a, b) => (SUBSPECIALTY_LABELS[a.subspecialty] || a.subspecialty).localeCompare(SUBSPECIALTY_LABELS[b.subspecialty] || b.subspecialty, "ko")).map((s) => (
              <button
                key={s.subspecialty}
                onClick={() => {
                  setSelectedSubspec(s.subspecialty);
                  setSelectedTopics(new Set());
                  updateURL(mode, s.subspecialty, new Set());
                }}
                className={`p-4 rounded-lg border text-center transition-colors ${
                  selectedSubspec === s.subspecialty
                    ? "border-primary bg-primary/10 text-primary font-semibold"
                    : "border-border hover:border-primary/50"
                }`}
              >
                <div className="font-medium">
                  {SUBSPECIALTY_LABELS[s.subspecialty] || s.subspecialty}
                </div>
                <div className="text-sm text-muted-foreground">{s.count}문항</div>
              </button>
            ))}
          </div>

          {/* 토픽 필터 */}
          {selectedSubspec && topicsBySubspec[selectedSubspec]?.length > 0 && (
            <div className="p-4 rounded-lg border bg-muted/30">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium">
                  세부 토픽 선택
                  <span className="text-muted-foreground font-normal ml-1">
                    (선택하지 않으면 전체 {subspecialties.find(s => s.subspecialty === selectedSubspec)?.count}문항)
                  </span>
                </p>
                {selectedTopics.size > 0 && (
                  <button
                    onClick={() => { setSelectedTopics(new Set()); updateURL(mode, selectedSubspec, new Set()); }}
                    className="text-xs text-muted-foreground hover:text-foreground"
                  >
                    초기화
                  </button>
                )}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {topicsBySubspec[selectedSubspec].map(({ topic, count }) => {
                  const isSelected = selectedTopics.has(topic);
                  return (
                    <button
                      key={topic}
                      onClick={() => {
                        const next = new Set(selectedTopics);
                        if (next.has(topic)) next.delete(topic);
                        else next.add(topic);
                        setSelectedTopics(next);
                        updateURL(mode, selectedSubspec, next);
                      }}
                      className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
                        isSelected
                          ? "bg-primary text-primary-foreground border-primary"
                          : "hover:bg-accent border-border"
                      }`}
                    >
                      {topic} ({count})
                    </button>
                  );
                })}
              </div>
              {selectedTopics.size > 0 && (
                <p className="text-xs text-primary mt-2">
                  {selectedTopics.size}개 토픽 선택 · {questionCount}문항
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* 시작 버튼 */}
      <div className="flex items-center gap-4 mt-6">
        <Button
          size="lg"
          onClick={startExam}
          disabled={
            (mode === "year" && !selectedYear) ||
            (mode === "subspecialty" && !selectedSubspec)
          }
        >
          {mode === "random"
            ? `랜덤 ${randomLimit}문항 시작`
            : `${questionCount}문항 시험 시작`}
        </Button>

        {session?.user && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => router.push("/exams/wrong-notes")}>
              오답노트
            </Button>
            <Button variant="outline" onClick={() => router.push("/exams/bookmarks")}>
              북마크
            </Button>
            <Button variant="ghost" onClick={() => router.push("/exams/results")}>
              시험 기록
            </Button>
          </div>
        )}
      </div>

      </>}

      {!session?.user && (
        <div className="mt-6 p-6 rounded-lg border border-dashed text-center">
          <p className="text-muted-foreground mb-3">
            기출문제 풀기는 로그인 후 이용할 수 있습니다.
          </p>
          <Button onClick={() => router.push("/login")}>로그인</Button>
        </div>
      )}
    </div>
  );
}
